import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, FileText, Download, Eye, User, Calendar,
  TrendingUp, DollarSign, BarChart3, PieChart, Building2,
  ChevronRight, Printer, FileSpreadsheet
} from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'
import { generateFinancialStatementPDF } from '../../utils/pdfGenerator'
import { exportStatementToExcel, exportStatementToCsv, exportStatementListToExcel } from '../../utils/statementUtils'
import AnalyticsDashboard from '../../components/AnalyticsDashboard'
import CumulativeReportPanel from '../../components/CumulativeReportPanel'

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const fmt = (amount) =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', minimumFractionDigits: 0 }).format(amount || 0)

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-UG', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

/* ─────────────────────────────────────────────
   Profit & Loss renderer
───────────────────────────────────────────── */
const ProfitLossStatement = ({ data }) => {
  const products = data?.products || []
  const expenses = data?.operating_expenses || []
  const totalRevenue = products.reduce((s, p) => s + (Number(p.revenue) || 0), 0)
  const totalCOGS = products.reduce((s, p) => s + (Number(p.cost_of_goods_sold) || 0), 0)
  const totalGrossProfit = products.reduce((s, p) => s + (Number(p.gross_profit) || 0), 0)
  const totalOpex = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const otherIncome = Number(data?.other_income) || 0
  const otherExpenses = Number(data?.other_expenses) || 0
  const netIncome = Number(data?.net_income) || (totalGrossProfit - totalOpex + otherIncome - otherExpenses)

  return (
    <div className="font-mono text-sm">
      {/* Revenue */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-2 pb-1 border-b border-gray-200">Revenue</div>
        {products.map((p, i) => (
          <div key={i} className="flex justify-between py-1 hover:bg-amber-50 px-1 rounded">
            <span className="text-gray-700 pl-4">{p.product_name}</span>
            <span className="text-gray-900 tabular-nums">{fmt(p.revenue)}</span>
          </div>
        ))}
        <div className="flex justify-between py-1.5 px-1 border-t border-gray-300 mt-1 font-semibold">
          <span className="text-gray-800">Total Revenue</span>
          <span className="tabular-nums">{fmt(totalRevenue)}</span>
        </div>
      </div>

      {/* COGS */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-2 pb-1 border-b border-gray-200">Cost of Goods Sold</div>
        {products.map((p, i) => (
          <div key={i} className="flex justify-between py-1 hover:bg-amber-50 px-1 rounded">
            <span className="text-gray-700 pl-4">{p.product_name}</span>
            <span className="text-gray-900 tabular-nums">{fmt(p.cost_of_goods_sold)}</span>
          </div>
        ))}
        <div className="flex justify-between py-1.5 px-1 border-t border-gray-300 mt-1 font-semibold">
          <span className="text-gray-800">Total COGS</span>
          <span className="tabular-nums">{fmt(totalCOGS)}</span>
        </div>
      </div>

      {/* Gross Profit */}
      <div className="flex justify-between py-2 px-2 bg-emerald-50 border border-emerald-200 rounded mb-6 font-bold">
        <span className="text-emerald-800">Gross Profit</span>
        <span className="text-emerald-700 tabular-nums">{fmt(totalGrossProfit)}</span>
      </div>

      {/* Operating Expenses */}
      {expenses.length > 0 && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-2 pb-1 border-b border-gray-200">Operating Expenses</div>
          {expenses.map((e, i) => (
            <div key={i} className="flex justify-between py-1 hover:bg-amber-50 px-1 rounded">
              <div className="pl-4">
                <span className="text-gray-700">{e.category || e.expense_category}</span>
                {(e.description || e.expense_description) && (
                  <span className="text-gray-400 text-xs ml-2">— {e.description || e.expense_description}</span>
                )}
              </div>
              <span className="text-gray-900 tabular-nums">{fmt(e.amount)}</span>
            </div>
          ))}
          <div className="flex justify-between py-1.5 px-1 border-t border-gray-300 mt-1 font-semibold">
            <span className="text-gray-800">Total Operating Expenses</span>
            <span className="tabular-nums text-red-600">({fmt(totalOpex)})</span>
          </div>
        </div>
      )}

      {/* Other income/expenses */}
      {(otherIncome !== 0 || otherExpenses !== 0) && (
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-2 pb-1 border-b border-gray-200">Other</div>
          {otherIncome !== 0 && (
            <div className="flex justify-between py-1 px-1">
              <span className="text-gray-700 pl-4">Other Income</span>
              <span className="tabular-nums">{fmt(otherIncome)}</span>
            </div>
          )}
          {otherExpenses !== 0 && (
            <div className="flex justify-between py-1 px-1">
              <span className="text-gray-700 pl-4">Other Expenses</span>
              <span className="tabular-nums text-red-600">({fmt(otherExpenses)})</span>
            </div>
          )}
        </div>
      )}

      {/* Net Income */}
      <div className={`flex justify-between py-3 px-3 rounded border-2 font-bold text-base ${netIncome >= 0 ? 'bg-emerald-600 border-emerald-700 text-white' : 'bg-red-600 border-red-700 text-white'}`}>
        <span>Net {netIncome >= 0 ? 'Income' : 'Loss'}</span>
        <span className="tabular-nums">{fmt(Math.abs(netIncome))}</span>
      </div>

      {data?.notes && <p className="mt-4 text-xs text-gray-500 italic border-t pt-3">Notes: {data.notes}</p>}
    </div>
  )
}

/* ─────────────────────────────────────────────
   Balance Sheet renderer
───────────────────────────────────────────── */
const BSRow = ({ label, value, indent, bold }) => (
  <div className={`flex justify-between py-1 px-1 rounded ${indent ? '' : ''} hover:bg-amber-50`}>
    <span className={`${indent ? 'pl-6' : ''} ${bold ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>{label}</span>
    <span className={`tabular-nums font-mono ${bold ? 'font-semibold text-gray-800' : 'text-gray-900'}`}>{fmt(value)}</span>
  </div>
)
const BSSectionHeader = ({ label }) => (
  <div className="text-xs uppercase tracking-widest text-gray-500 font-sans mb-2 pb-1 border-b border-gray-200 mt-6 first:mt-0">{label}</div>
)
const BSSubHeader = ({ label }) => (
  <div className="mb-1 mt-3 text-xs font-sans text-gray-400 pl-2">{label}</div>
)
const BSTotalRow = ({ label, value, color = 'gray' }) => {
  const colors = { gray: 'border-t border-gray-300', emerald: 'bg-emerald-50 border border-emerald-200 rounded mt-2', blue: 'bg-blue-50 border border-blue-200 rounded mt-2' }
  return (
    <div className={`flex justify-between py-1.5 px-1 mt-1 font-semibold ${colors[color]}`}>
      <span className={color === 'emerald' ? 'text-emerald-800' : color === 'blue' ? 'text-blue-800' : 'text-gray-800'}>{label}</span>
      <span className={`tabular-nums ${color === 'emerald' ? 'text-emerald-700' : color === 'blue' ? 'text-blue-700' : ''}`}>{fmt(value)}</span>
    </div>
  )
}

const BalanceSheetStatement = ({ data }) => {
  if (!data) return null
  const d = data
  return (
    <div className="font-mono text-sm">
      <BSSectionHeader label="Assets" />
      <BSSubHeader label="Current Assets" />
      <BSRow label="Cash & Equivalents" value={d.cash_and_equivalents} indent />
      <BSRow label="Accounts Receivable" value={d.accounts_receivable} indent />
      <BSRow label="Inventory" value={d.inventory} indent />
      <BSRow label="Other Current Assets" value={d.other_current_assets} indent />
      <BSTotalRow label="Total Current Assets" value={d.total_current_assets} />
      <BSSubHeader label="Non-Current Assets" />
      <BSRow label="Fixed Assets" value={d.fixed_assets} indent />
      <BSRow label="Accumulated Depreciation" value={d.accumulated_depreciation} indent />
      <BSRow label="Net Fixed Assets" value={d.net_fixed_assets} indent />
      <BSRow label="Other Assets" value={d.other_assets} indent />
      <BSTotalRow label="Total Assets" value={d.total_assets} color="blue" />

      <BSSectionHeader label="Liabilities" />
      <BSSubHeader label="Current Liabilities" />
      <BSRow label="Accounts Payable" value={d.accounts_payable} indent />
      <BSRow label="Short-Term Debt" value={d.short_term_debt} indent />
      <BSRow label="Other Current Liabilities" value={d.other_current_liabilities} indent />
      <BSTotalRow label="Total Current Liabilities" value={d.total_current_liabilities} />
      <BSSubHeader label="Non-Current Liabilities" />
      <BSRow label="Long-Term Debt" value={d.long_term_debt} indent />
      <BSRow label="Other Liabilities" value={d.other_liabilities} indent />
      <BSTotalRow label="Total Liabilities" value={d.total_liabilities} />

      <BSSectionHeader label="Owner's Equity" />
      <BSRow label="Owner's Equity" value={d.owner_equity} indent />
      <BSRow label="Retained Earnings" value={d.retained_earnings} indent />
      <BSTotalRow label="Total Equity" value={d.total_equity} />

      <div className="mt-4 flex justify-between py-3 px-3 rounded border-2 border-blue-700 bg-blue-700 text-white font-bold text-base">
        <span>Total Liabilities + Equity</span>
        <span className="tabular-nums">{fmt((Number(d.total_liabilities) || 0) + (Number(d.total_equity) || 0))}</span>
      </div>

      {d.notes && <p className="mt-4 text-xs text-gray-500 italic border-t pt-3">Notes: {d.notes}</p>}
    </div>
  )
}

/* ─────────────────────────────────────────────
   Statement document (detail view)
───────────────────────────────────────────── */
const StatementDocument = ({ statement, onBack, onDownload, onExportExcel, onExportCsv, onAnalytics, downloadingPDF }) => {
  const isPL = statement.statement_type === 'profit_loss'
  const data = statement.financial_data || {}
  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      <div className="max-w-4xl mx-auto px-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <button onClick={onBack} className="flex items-center text-sm text-gray-600 hover:text-gray-900 gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Statements
          </button>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
              <Printer className="h-4 w-4" /> Print
            </button>
            <button onClick={onAnalytics} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
              <BarChart3 className="h-4 w-4" /> Analytics
            </button>
            <button onClick={onExportExcel} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
              <FileSpreadsheet className="h-4 w-4" /> XLSX
            </button>
            <button onClick={onExportCsv} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
              <FileText className="h-4 w-4" /> CSV
            </button>
            <button onClick={onDownload} disabled={downloadingPDF} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Download className="h-4 w-4" />
              {downloadingPDF ? 'Generating…' : 'Export PDF'}
            </button>
          </div>
        </div>

        {/* Paper */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden print:shadow-none print:rounded-none">
          {/* Letterhead */}
          <div className={`px-10 py-8 ${isPL ? 'bg-emerald-700' : 'bg-blue-700'} text-white`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-5 w-5 opacity-80" />
                  <span className="text-sm opacity-80 tracking-wide uppercase">{statement.company_name || 'UFCC Microfinance'}</span>
                </div>
                <h1 className="text-2xl font-bold tracking-tight">
                  {isPL ? 'Statement of Profit & Loss' : 'Balance Sheet'}
                </h1>
                <p className="text-sm opacity-75 mt-0.5">
                  {isPL
                    ? `For the period: ${data?.period_start || ''} – ${data?.period_end || statement.reporting_period}`
                    : `As at: ${data?.as_of_date || statement.reporting_period}`}
                </p>
              </div>
              <div className="text-right text-sm opacity-80">
                <p className="text-xs uppercase tracking-wide mb-0.5">Prepared for</p>
                <p className="font-semibold text-base">{statement.customer_name}</p>
                <p className="text-xs">Member No: {statement.member_number || statement.customer_id}</p>
                <p className="text-xs mt-1">Issued: {fmtDate(statement.created_at)}</p>
              </div>
            </div>
          </div>
          <div className={`h-1 ${isPL ? 'bg-emerald-400' : 'bg-blue-400'}`} />

          {/* Body */}
          <div className="px-10 py-8">
            {isPL ? <ProfitLossStatement data={data} /> : <BalanceSheetStatement data={data} />}
          </div>

          {/* Footer */}
          <div className="px-10 py-4 bg-gray-50 border-t text-xs text-gray-400 flex justify-between">
            <span>Statement ID: {statement.id}</span>
            <span>Generated by UFCC Microfinance System</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const AdminFinancialStatements = () => {
  const { admin } = useAdminAuth()
  const [statements, setStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedStatement, setSelectedStatement] = useState(null)
  const [viewMode, setViewMode] = useState('list')
  const [filter, setFilter] = useState('all')
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => { fetchFinancialStatements() }, [])

  const normalizeStatement = (s) => ({
    id: s.id,
    statement_type: s.statementType || s.statement_type,
    customer_name: s.customerName || s.customer_name,
    customer_id: s.customerId || s.customer_id,
    company_id: s.companyId || s.company_id,
    company_name: s.companyName || s.company_name,
    reporting_period: s.reporting_period || s.reportingPeriod || '',
    financial_data: s.data || s.financial_data || {},
    created_at: s.createdAt || s.created_at,
    member_number: s.memberNumber || s.member_number,
  })

  const fetchFinancialStatements = async () => {
    try {
      setLoading(true)
      const response = await apiService.getFinancialStatements()
      if (response.success || response.status === 'SUCCESS') {
        setStatements((response.data || []).map(normalizeStatement))
      }
    } catch (error) {
      console.error('Failed to fetch financial statements:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatementTypeLabel = (type) =>
    type === 'profit_loss' ? 'Profit & Loss' : 'Balance Sheet'

  const downloadStatementPDF = async (statement) => {
    try {
      setDownloadingPDF(true)
      await generateFinancialStatementPDF({
        customerName: statement.customer_name,
        memberId: statement.member_number,
        statementType: statement.statement_type,
        reportingPeriod: statement.reporting_period,
        createdAt: statement.created_at,
        financialData: statement.financial_data,
        companyName: statement.company_name || 'UFCC Microfinance',
      })
    } catch (error) {
      alert('Failed to download PDF: ' + error.message)
    } finally {
      setDownloadingPDF(false)
    }
  }

  const exportStatementExcel = (statement) => {
    try {
      exportStatementToExcel(statement, `statement_${statement.id || 'unknown'}.xlsx`)
    } catch (error) {
      alert('Failed to export XLSX: ' + error.message)
    }
  }

  const exportStatementCsv = (statement) => {
    try {
      exportStatementToCsv(statement, `statement_${statement.id || 'unknown'}.csv`)
    } catch (error) {
      alert('Failed to export CSV: ' + error.message)
    }
  }

  const filteredStatements = statements.filter((s) => {
    const matchType = filter === 'all' || s.statement_type === filter
    const matchSearch = !search ||
      s.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      String(s.member_number || '').toLowerCase().includes(search.toLowerCase())
    return matchType && matchSearch
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600" />
      </div>
    )
  }

  if (viewMode === 'detail' && selectedStatement) {
    return (
      <StatementDocument
        statement={selectedStatement}
        onBack={() => setViewMode('list')}
        onDownload={() => downloadStatementPDF(selectedStatement)}
        onExportExcel={() => exportStatementExcel(selectedStatement)}
        onExportCsv={() => exportStatementCsv(selectedStatement)}
        onAnalytics={() => setViewMode('analytics')}
        downloadingPDF={downloadingPDF}
      />
    )
  }

  if (viewMode === 'cumulative') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center gap-3">
            <button onClick={() => setViewMode('list')} className="flex items-center text-sm text-gray-600 hover:text-gray-900 gap-1">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Cumulative Member Report</h1>
          </div>
          <CumulativeReportPanel isAdmin />
        </div>
      </div>
    )
  }

  if (viewMode === 'analytics') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-center gap-3">
            <button onClick={() => setViewMode('list')} className="flex items-center text-sm text-gray-600 hover:text-gray-900 gap-1">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Financial Analytics</h1>
          </div>
          <AnalyticsDashboard financialStatements={statements} />
        </div>
      </div>
    )
  }

  const plCount = statements.filter(s => s.statement_type === 'profit_loss').length
  const bsCount = statements.filter(s => s.statement_type === 'balance_sheet').length

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky page header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-400 hover:text-gray-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Financial Statements</h1>
              <p className="text-xs text-gray-500">MG: {admin?.mgCode}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search member…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 w-44"
            />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="all">All Types</option>
              <option value="profit_loss">Profit &amp; Loss</option>
              <option value="balance_sheet">Balance Sheet</option>
            </select>
            <button onClick={() => setViewMode('analytics')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700">
              <BarChart3 className="h-4 w-4" /> Analytics
            </button>
            <button onClick={() => setViewMode('cumulative')}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700">
              <Calendar className="h-4 w-4" /> Cumulative
            </button>
            <button
              onClick={() => exportStatementListToExcel(filteredStatements, `financial_statements_${admin?.mgCode || 'mg'}.xlsx`)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700">
              <FileSpreadsheet className="h-4 w-4" /> Export XLSX
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-gray-100"><FileText className="h-5 w-5 text-gray-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{statements.length}</p>
              <p className="text-xs text-gray-500">Total Statements</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-50"><TrendingUp className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{plCount}</p>
              <p className="text-xs text-gray-500">Profit &amp; Loss</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-5 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-50"><DollarSign className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{bsCount}</p>
              <p className="text-xs text-gray-500">Balance Sheets</p>
            </div>
          </div>
        </div>

        {/* Statement list */}
        {filteredStatements.length > 0 ? (
          <div className="space-y-3">
            {filteredStatements.map((s) => {
              const isPL = s.statement_type === 'profit_loss'
              const products = s.financial_data?.products || []
              const totalRevenue = products.reduce((acc, p) => acc + (Number(p.revenue) || 0), 0)
              return (
                <div key={s.id} className="bg-white rounded-xl border hover:border-blue-300 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-0">
                    <div className={`w-1.5 self-stretch rounded-l-xl flex-shrink-0 ${isPL ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                    <div className="flex-1 px-5 py-4 flex items-center gap-4">
                      <div className={`p-2.5 rounded-lg flex-shrink-0 ${isPL ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                        {isPL
                          ? <TrendingUp className="h-5 w-5 text-emerald-600" />
                          : <DollarSign className="h-5 w-5 text-blue-600" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900 text-sm">{s.customer_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPL ? 'text-emerald-700 bg-emerald-50' : 'text-blue-700 bg-blue-50'}`}>
                            {getStatementTypeLabel(s.statement_type)}
                          </span>
                        </div>
                        <div className="flex gap-4 mt-0.5 text-xs text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{s.reporting_period}</span>
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />#{s.member_number || s.customer_id}</span>
                          <span>Submitted {fmtDate(s.created_at)}</span>
                        </div>
                      </div>
                      {isPL && totalRevenue > 0 && (
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-gray-400">Revenue</p>
                          <p className="text-sm font-semibold text-gray-900">{fmt(totalRevenue)}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => downloadStatementPDF(s)} disabled={downloadingPDF}
                          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 disabled:opacity-40" title="Export PDF">
                          <Download className="h-4 w-4" />
                        </button>
                        <button onClick={() => exportStatementExcel(s)}
                          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500" title="Export XLSX">
                          <FileSpreadsheet className="h-4 w-4" />
                        </button>
                        <button onClick={() => { setSelectedStatement(s); setViewMode('detail') }}
                          className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700">
                          <Eye className="h-4 w-4" /> View <ChevronRight className="h-3 w-3 opacity-60" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl border py-20 text-center">
            <FileText className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="font-medium text-gray-500">No statements found</p>
            <p className="text-sm text-gray-400 mt-1">
              {search ? `No results for "${search}"` : filter !== 'all' ? `No ${getStatementTypeLabel(filter)} statements yet` : 'No financial statements submitted yet.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminFinancialStatements
